"use client";

import { createContext, type ReactNode, useContext, useMemo } from "react";

export type PricingJourneyContextValue = Readonly<{
  intent: "DISCOVER" | "PRODUCTION_ACTIVATION";
  productionDemoPublicId?: string;
}>;

const PricingJourneyContext = createContext<
  PricingJourneyContextValue | undefined
>(undefined);

/** Resolves Pricing presentation from the server-validated page input. */
export function resolvePricingJourney(
  productionDemoPublicId?: string,
): PricingJourneyContextValue {
  if (productionDemoPublicId) {
    return {
      intent: "PRODUCTION_ACTIVATION",
      productionDemoPublicId,
    };
  }
  return { intent: "DISCOVER" };
}

/**
 * Shares the visitor's Pricing intent across presentation components.
 * Production conversion can therefore hide acquisition prompts and preserve
 * the selected demo without each component parsing the URL independently.
 */
export function PricingJourneyProvider({
  productionDemoPublicId,
  children,
}: {
  productionDemoPublicId?: string;
  children: ReactNode;
}) {
  const contextValue = useMemo(
    () => resolvePricingJourney(productionDemoPublicId),
    [productionDemoPublicId],
  );

  return (
    <PricingJourneyContext.Provider value={contextValue}>
      {children}
    </PricingJourneyContext.Provider>
  );
}

/** Reads the single presentation context owned by the Pricing page. */
export function usePricingJourney(): PricingJourneyContextValue {
  const contextValue = useContext(PricingJourneyContext);
  if (!contextValue) {
    throw new Error("PRICING_JOURNEY_PROVIDER_REQUIRED");
  }
  return contextValue;
}
